import React, { useState, useEffect } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import Select from "react-select";
import dayjs from "dayjs";
import { useAcademyTopics, useProposeTopic } from "../hooks/useAcademyTopics";
import { useSedes } from "../hooks/useSedes";
import { useTeachers } from "../hooks/usePlannings";
import { useClassrooms } from "../../academy/hooks/useClassrooms";
import { useLevels } from "../../academy/hooks/useLevels";
import { useAuth } from "../../../app/providers/AuthProvider";
import { DateField } from "../../../components/DateField";

const INITIAL = {
  date: "",
  time_start: "",
  duration: 1,
  headquarters: null,
  classroom: null,
  instructor: null,
  level: null,
  description: "",
  topics: [],
  repeat: false, // D7 — repetir semanalmente (solo al crear)
  repeatWeeks: 4,
};

function CreateClassModal({
  show,
  onHide,
  onCreate,
  isLoading,
  error,
  mode = "create",
  initial = null,
  onDelete = null, // solo propietario (edición): muestra el botón Eliminar
}) {
  const isEdit = mode === "edit";
  const todayISO = new Date().toISOString().slice(0, 10);
  const { user } = useAuth();
  // Only owners choose the instructor. When a teacher creates a class, they are
  // assumed to be the instructor (the backend defaults it to the creator).
  const isOwner = user?.role === "OWNER";
  const [form, setForm] = useState(INITIAL);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposeName, setProposeName] = useState("");
  const [proposeMsg, setProposeMsg] = useState(null);
  const { data: topicsData } = useAcademyTopics();
  const { data: sedesData } = useSedes();
  const { data: teachersData } = useTeachers();
  const { data: classroomsData } = useClassrooms();
  const { data: levelsData } = useLevels();
  const proposeMutation = useProposeTopic();

  const levelOptions = (levelsData ?? []).map((l) => ({
    value: l.id,
    label: l.name,
  }));

  const sedeOptions = (sedesData ?? []).map((s) => ({
    value: s.id,
    label: s.reference_name || s.address || `Sede ${s.id}`,
  }));

  const teacherOptions = (teachersData ?? []).map((t) => ({
    value: t.id,
    label: t.name,
  }));

  // In edit mode, hydrate the form from the class being edited (once, on open).
  useEffect(() => {
    if (!show) return;
    if (isEdit && initial) {
      setForm({
        date: initial.date ?? "",
        time_start: initial.time_start ?? "",
        duration: initial.duration ?? 1,
        headquarters: initial.headquarters_id
          ? { value: initial.headquarters_id, label: initial.place ?? "Sede" }
          : null,
        instructor: initial.instructor
          ? { value: initial.instructor.id, label: initial.instructor.name }
          : null,
        classroom: initial.classroom
          ? { value: initial.classroom.id, label: initial.classroom.name }
          : null,
        level: initial.level
          ? { value: initial.level.id, label: initial.level.name }
          : null,
        description:
          initial.description && initial.description !== "Clase programada"
            ? initial.description
            : "",
        topics: (initial.topics ?? [])
          .filter((t) => t.id != null)
          .map((t) => ({
            value: t.academy_topic_id ?? t.id,
            label: t.name,
          })),
      });
    } else if (!isEdit) {
      // Nueva clase: por defecto hoy y la siguiente hora en punto (5:17 → 6:00).
      // Si viene un prefill (crear desde un hueco del calendario), se usa este.
      const next = dayjs().add(1, "hour").startOf("hour");
      setForm({
        ...INITIAL,
        date: initial?.date ?? next.format("YYYY-MM-DD"),
        time_start: initial?.time_start ?? next.format("HH:mm"),
        duration: initial?.duration ?? INITIAL.duration,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, isEdit, initial?.id]);

  // Default to the only sede when the academy has exactly one (create mode).
  useEffect(() => {
    if (!isEdit && !form.headquarters && sedeOptions.length === 1) {
      setForm((f) => ({ ...f, headquarters: sedeOptions[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sedesData, show]);

  // Default instructor to the logged-in user when they're one of the academy's
  // teachers (create mode) — most classes created from "Mi agenda" are the
  // creator's own, so forcing a manual pick every time is pure friction.
  // `user.id` comes from the JWT's `user_id` claim; `t.user_id` is the User pk
  // behind each teacher Profile (see TeacherListView) — they're comparable.
  useEffect(() => {
    if (isEdit || !isOwner || form.instructor || !teachersData || user?.id == null) {
      return;
    }
    const mine = teachersData.find((t) => String(t.user_id) === String(user.id));
    if (mine) {
      setForm((f) => ({ ...f, instructor: { value: mine.id, label: mine.name } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teachersData, show, isOwner]);

  const handlePropose = () => {
    const name = proposeName.trim();
    if (!name) return;
    setProposeMsg(null);
    proposeMutation.mutate(
      { name },
      {
        onSuccess: () => {
          setProposeMsg({
            type: "success",
            text: "Propuesta enviada. El administrador la revisará.",
          });
          setProposeName("");
          setProposeOpen(false);
        },
        onError: () =>
          setProposeMsg({
            type: "danger",
            text: "No se pudo enviar la propuesta.",
          }),
      }
    );
  };

  const topicOptions = (topicsData ?? []).map((t) => ({
    value: t.id,
    label: t.topic?.name ?? t.name ?? t.id,
  }));

  // Aulas de la sede elegida (solo activas). El aula es específica de una sede.
  const selectedHqId = form.headquarters?.value;
  const classroomOptions = (classroomsData ?? [])
    .filter((c) => c.headquarters === selectedHqId && c.is_active)
    .map((c) => ({
      value: c.id,
      label: c.capacity != null ? `${c.name} · cupo ${c.capacity}` : c.name,
    }));

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      date: form.date,
      time_start: form.time_start || undefined,
      duration: Number(form.duration) || 1,
      headquarters: form.headquarters?.value,
      // El aula la puede fijar tanto el docente como el propietario (opcional).
      classroom: form.classroom?.value ?? null,
      // Nivel de ESTA clase (no el del docente) — para que el estudiante pueda
      // compararlo contra el suyo. Opcional: sin elegirlo, el backend cae al
      // nivel del docente (compatibilidad con clases creadas antes de esto).
      level: form.level?.value ?? null,
      description: form.description,
      topics: form.topics.map((t) => t.value),
    };
    // Only owners set the instructor. For a teacher, it's left out so the backend
    // keeps the class's instructor (edit) or defaults to the creator (create).
    if (isOwner) {
      payload.instructor = form.instructor?.value ?? null;
    }
    // D7 — recurrencia semanal (solo al crear). El backend genera una clase por
    // semana y omite las que caigan en conflicto o en el pasado.
    if (!isEdit && form.repeat) {
      payload.repeat_weeks = Math.max(2, Math.min(Number(form.repeatWeeks) || 2, 26));
    }
    onCreate(payload);
  };

  const handleHide = () => {
    setForm(INITIAL);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? "Editar clase" : "Nueva clase"}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error?.response?.data?.detail ??
                error.message ??
                (isEdit ? "Error al editar la clase." : "Error al crear la clase.")}
            </Alert>
          )}
          {isEdit && (
            <Alert variant="light" className="border small text-muted mb-3">
              Si esta clase tiene estudiantes inscritos, se les notificará de los
              cambios.
            </Alert>
          )}
          <div className="row g-3">
            <Form.Group controlId="class-date" className="col-12 col-md-4">
            <Form.Label>Fecha *</Form.Label>
            <DateField
              id="class-date"
              value={form.date}
              // Owners may backfill past classes; teachers can't pick the past.
              min={isOwner ? undefined : todayISO}
              onChange={(v) => setForm((f) => ({ ...f, date: v }))}
              required
            />
          </Form.Group>
            <Form.Group controlId="class-time" className="col-6 col-md-4">
            <Form.Label>Hora de inicio</Form.Label>
            <Form.Control
              type="time"
              value={form.time_start}
              onChange={handleChange("time_start")}
              style={{ minHeight: 44 }}
            />
          </Form.Group>
            <Form.Group controlId="class-duration" className="col-6 col-md-4">
            <Form.Label>Duración (horas)</Form.Label>
            <Form.Control
              type="number"
              min="1"
              step="1"
              value={form.duration}
              onChange={handleChange("duration")}
              style={{ minHeight: 44 }}
            />
          </Form.Group>
          {!isEdit && (
            <Form.Group controlId="class-repeat" className="col-12">
              <Form.Check
                type="checkbox"
                id="class-repeat"
                label="Repetir semanalmente"
                checked={form.repeat}
                onChange={(e) =>
                  setForm((f) => ({ ...f, repeat: e.target.checked }))
                }
                style={{ accentColor: "var(--kp-primary)" }}
              />
              {form.repeat && (
                <div className="d-flex align-items-center gap-2 mt-2">
                  <span className="text-muted small">Crear</span>
                  <Form.Control
                    type="number"
                    min="2"
                    max="26"
                    step="1"
                    value={form.repeatWeeks}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, repeatWeeks: e.target.value }))
                    }
                    style={{ width: 90, minHeight: 40 }}
                  />
                  <span className="text-muted small">
                    clases, una por semana (mismo día y hora).
                  </span>
                </div>
              )}
              {form.repeat && (
                <Form.Text muted>
                  Las semanas que caigan en conflicto de horario o en el pasado se
                  omiten automáticamente.
                </Form.Text>
              )}
            </Form.Group>
          )}
            <Form.Group controlId="class-sede" className="col-12 col-md-6">
            <Form.Label>Sede *</Form.Label>
            <Select
              inputId="class-sede"
              options={sedeOptions}
              value={form.headquarters}
              onChange={(opt) =>
                // Cambiar de sede limpia el aula: las aulas son propias de una sede.
                setForm((f) => ({ ...f, headquarters: opt, classroom: null }))
              }
              placeholder={
                sedeOptions.length === 0
                  ? "No hay sedes registradas"
                  : "Selecciona la sede…"
              }
              noOptionsMessage={() => "No hay sedes registradas"}
              classNamePrefix="react-select"
            />
          </Form.Group>
            <Form.Group controlId="class-classroom" className="col-12 col-md-6">
            <Form.Label>Aula</Form.Label>
            <Select
              inputId="class-classroom"
              options={classroomOptions}
              value={form.classroom}
              onChange={(opt) => setForm((f) => ({ ...f, classroom: opt }))}
              isClearable
              isDisabled={!form.headquarters}
              placeholder={
                !form.headquarters
                  ? "Primero selecciona la sede…"
                  : classroomOptions.length === 0
                    ? "No hay aulas en esta sede"
                    : "Selecciona el aula (opcional)…"
              }
              noOptionsMessage={() => "No hay aulas en esta sede"}
              classNamePrefix="react-select"
            />
          </Form.Group>
          {isOwner && (
              <Form.Group controlId="class-instructor" className="col-12 col-md-6">
              <Form.Label>Instructor</Form.Label>
              <Select
                inputId="class-instructor"
                options={teacherOptions}
                value={form.instructor}
                onChange={(opt) => setForm((f) => ({ ...f, instructor: opt }))}
                isClearable
                placeholder="Selecciona el docente…"
                noOptionsMessage={() => "No hay docentes registrados"}
                classNamePrefix="react-select"
              />
            </Form.Group>
          )}
            <Form.Group controlId="class-level" className="col-12 col-md-6">
            <Form.Label>Nivel</Form.Label>
            <Select
              inputId="class-level"
              options={levelOptions}
              value={form.level}
              onChange={(opt) => setForm((f) => ({ ...f, level: opt }))}
              isClearable
              placeholder="Selecciona el nivel de la clase (opcional)…"
              noOptionsMessage={() => "No hay niveles registrados"}
              classNamePrefix="react-select"
            />
            <Form.Text muted>
              Ayuda al estudiante a saber si la clase corresponde a su nivel,
              está por encima o por debajo.
            </Form.Text>
          </Form.Group>
            <Form.Group controlId="class-topics" className="col-12">
            <Form.Label>Temas</Form.Label>
            <Select
              isMulti
              options={topicOptions}
              value={form.topics}
              onChange={(selected) =>
                setForm((f) => ({ ...f, topics: selected ?? [] }))
              }
              placeholder="Selecciona temas de la academia…"
              classNamePrefix="react-select"
            />
            <div className="mt-2">
              {!proposeOpen && (
                <Button
                  variant="link"
                  className="p-0 small"
                  onClick={() => {
                    setProposeMsg(null);
                    setProposeOpen(true);
                  }}
                >
                  ¿No encuentras un tema? Propón uno
                </Button>
              )}
              {proposeOpen && (
                <div className="d-flex gap-2 align-items-start mt-1">
                  <Form.Control
                    size="sm"
                    value={proposeName}
                    onChange={(e) => setProposeName(e.target.value)}
                    placeholder="Nombre del tema propuesto"
                  />
                  <Button
                    size="sm"
                    variant="outline-primary"
                    style={{ minHeight: 38 }}
                    onClick={handlePropose}
                    disabled={proposeMutation.isPending || !proposeName.trim()}
                  >
                    {proposeMutation.isPending ? "…" : "Proponer"}
                  </Button>
                  <Button
                    size="sm"
                    variant="link"
                    className="text-muted"
                    onClick={() => setProposeOpen(false)}
                  >
                    Cancelar
                  </Button>
                </div>
              )}
              {proposeMsg && (
                <div
                  className={`small mt-1 text-${
                    proposeMsg.type === "success" ? "success" : "danger"
                  }`}
                >
                  {proposeMsg.text}
                </div>
              )}
            </div>
          </Form.Group>
            <Form.Group controlId="class-description" className="col-12">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.description}
                onChange={handleChange("description")}
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer
          className={
            onDelete && isEdit ? "justify-content-between" : undefined
          }
        >
          {onDelete && isEdit && (
            <Button
              variant="outline-danger"
              onClick={onDelete}
              style={{ minHeight: 44 }}
            >
              <i className="mdi mdi-trash-can-outline me-1" aria-hidden="true" />
              Eliminar
            </Button>
          )}
          <div className="d-flex gap-2">
            <Button variant="light" onClick={handleHide} style={{ minHeight: 44 }}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
              style={{ minHeight: 44 }}
            >
              {isLoading ? (
                <Spinner size="sm" />
              ) : isEdit ? (
                "Guardar cambios"
              ) : (
                "Crear clase"
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export { CreateClassModal };
